/**
 * Walk-in counter sale API wrapper (DNKADM-0308; DNKAPI-0497 rewires this endpoint onto the
 * order surface — packages and memberships, not only credits).
 *
 * `POST /v2/admin/credit-purchases/walk-in` keeps its path (route unchanged). It provisions a
 * brand-new contact from name + email, auto-invites them, then delegates the cart to the SAME
 * order surface the member sell-cart uses (`AdminMemberOrderService`, via `AdminWalkInCreditSaleService.sell`) —
 * so a walk-in can now be sold a package or a membership plan, not only credits.
 *
 * **The wire shape is now the order surface's own shape, adopted verbatim.** This module reuses
 * `membershipOrders.ts`'s `OrderLineInput` / `OrderPaymentInput` / `OverrideAuthInput` types and
 * its `parseOrder` parser directly, rather than declaring a parallel shape or a second parser
 * (DNKAPI-0497 delivery: "adopts `AdminOrderRequest.Line`/`.Payment`/`.OverrideAuth` verbatim").
 *
 * What is genuinely still different about this surface, even after 0497:
 *  - **No pricing/quote endpoint.** A walk-in has no `contactId` until the sale is submitted, so
 *    there is nothing to quote against — this limitation survives 0497 (verified 2026-08-28 in
 *    `membershipOrders.ts`'s `getMembershipQuote`, which REQUIRES a `contactId`). Any cart
 *    estimate shown before submit is computed client-side and labelled "before tax."
 *  - **Card mode never offers a card on file.** A walk-in has no member record yet, so every
 *    card charge tokenizes fresh — unchanged from before 0497.
 *
 * Every constraint the order surface enforces is now inherited, since the sale is delegated
 * rather than reimplemented: a `membership_plan` line cannot mix with a `package`/`credit` line
 * in the same cart (400 `UNSUPPORTED_IN_SLICE`); `external` mode rejects a `membership_plan`
 * line (a subscription's renewals cannot bill against cash in a drawer); a `credit` line carrying
 * `overrideBase` is 400 `OVERRIDE_NOT_SUPPORTED_ON_LINE`; and `payment.idempotencyKey` is
 * REQUIRED for every mode (a tightening — it used to be required only for `card`).
 *
 * The response is the order surface's own receipt (`orderId`, `orderNumber`, `status`,
 * `currency`, `subtotal`, `taxTotal`, `grandTotal`, `compValue`, `lines`, `payments`,
 * `paymentAction`) **plus** the walk-in-specific outcomes `contactId`, `invited`, and
 * (DNKAPI-0508) `contactMatched`/`matchedFirstName`/`matchedLastName` — parsed by reusing
 * `membershipOrders.ts`'s `parseOrder` and extending its result, rather than a second parser.
 *
 * **DNKAPI-0508 — the server never silently renames a matched contact.** Provisioning resolves
 * the contact by normalized email FIRST: on a match the contact's STORED name is kept and the
 * typed name is used for nothing; only a genuinely new email creates a contact with the typed
 * name. `contactMatched` tells the caller which happened, and `matchedFirstName`/`matchedLastName`
 * carry the kept name (present only when `contactMatched` is `true`) so DNKADM-0354's confirmation
 * can say who the sale actually landed on ("This is Peyton1 Dowdle") instead of implying a fresh
 * signup.
 *
 * Bearer-only `/v2/admin/**` surface — routed + audited exactly like every other v2 admin call
 * (`request()`, the mutating `X-Session-Mode` stamp).
 */
import { request } from './client';
import { ApiError } from './errors';
import { v2ErrorCode } from './v2Errors';
import { serverMessage } from './serverMessage';
import { CardTokenizationError } from './payments';
import { declineReasonLineFromError } from '@/utils/declineReason';
import {
  parseOrder,
  type MembershipOrder,
  type OrderLineInput,
  type OrderPaymentInput,
  type OverrideAuthInput,
} from './membershipOrders';

const PATH = '/v2/admin/credit-purchases/walk-in';

/** The walk-in's identity to provision + invite. `firstName`/`lastName`/`email` are required. */
export interface WalkInContactInput {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
}

/**
 * One cart line — the order surface's shape (`membership_plan` | `package` | `credit`),
 * reused verbatim from `membershipOrders.ts` (DNKAPI-0497).
 */
export type WalkInLineInput = OrderLineInput;

/**
 * How the cart settles — the order surface's shape (`card` | `comp` | `external`), reused
 * verbatim from `membershipOrders.ts` (DNKAPI-0497). Card mode carries only the fresh-token
 * variant on this surface; a walk-in has no card on file to name.
 */
export type WalkInPaymentInput = OrderPaymentInput;

export interface CreateWalkInSaleInput {
  contact: WalkInContactInput;
  lines: WalkInLineInput[];
  payment: WalkInPaymentInput;
  /** One transaction-level PIN whenever any line carries `overrideBase` (DNKAPI-0495/0496). */
  overrideAuth?: OverrideAuthInput;
}

/**
 * The order receipt plus the two walk-in-specific outcomes (DNKAPI-0497). Everything from
 * `orderId` through `paymentAction` is the SAME `MembershipOrder` shape the member sell-cart's
 * receipt carries.
 */
export interface WalkInSaleResult extends MembershipOrder {
  /** The provisioned (create-or-find by email) contact the cart was sold to; always present. */
  contactId: string | null;
  /** `true` when an invitation was created THIS call; `false` when the email already had access or a pending invite. */
  invited: boolean;
  /**
   * `true` when the typed email already belonged to an existing contact (DNKAPI-0508):
   * the contact's STORED name was kept and the typed name was used for nothing. `false` when the
   * email was genuinely new and the contact was created with the typed name. Lets the confirmation
   * say who the sale actually landed on instead of assuming a fresh signup (DNKADM-0354).
   */
  contactMatched: boolean;
  /** The matched contact's stored first name that was kept; present only when `contactMatched`. */
  matchedFirstName: string | null;
  /** The matched contact's stored last name that was kept; present only when `contactMatched`. */
  matchedLastName: string | null;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function asText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

/** Reuse `membershipOrders.ts`'s order parser and extend it with the walk-in-specific fields. */
function parseWalkInSaleResult(body: unknown): WalkInSaleResult {
  const order = parseOrder(body);
  const o = isObject(body) ? body : {};
  return {
    ...order,
    contactId: asText(o['contactId']),
    invited: o['invited'] === true,
    contactMatched: o['contactMatched'] === true,
    matchedFirstName: asText(o['matchedFirstName']),
    matchedLastName: asText(o['matchedLastName']),
  };
}

function toContactBody(contact: WalkInContactInput): Record<string, unknown> {
  const body: Record<string, unknown> = {
    firstName: contact.firstName.trim(),
    lastName: contact.lastName.trim(),
    email: contact.email.trim(),
  };
  const phone = contact.phone?.trim();
  if (phone) body['phone'] = phone;
  return body;
}

/**
 * `POST /v2/admin/credit-purchases/walk-in` → provision + auto-invite the walk-in contact and
 * sell them the cart in one call — the order surface's own request/response shape (DNKAPI-0497).
 * `idempotencyKey` is required on `payment` for every mode; `overrideAuth` is sent only when
 * supplied. Throws a typed `ApiError` on a non-2xx — `402` on a card decline (no grant), `400
 * INVALID_REQUEST` on a missing contact field OR a rejected cart/payment shape (both share this
 * code on this surface — see {@link toWalkInSaleErrorView}), `400 UNSUPPORTED_IN_SLICE` on a
 * `membership_plan` line mixed with a one-time line, `503 CONTACT_UNAVAILABLE` when the contact
 * could not be provisioned (no invite, no charge).
 */
export async function createWalkInSale(input: CreateWalkInSaleInput): Promise<WalkInSaleResult> {
  const body = await request<unknown>('POST', PATH, {
    json: {
      contact: toContactBody(input.contact),
      lines: input.lines,
      payment: input.payment,
      ...(input.overrideAuth === undefined ? {} : { overrideAuth: input.overrideAuth }),
    },
  });
  return parseWalkInSaleResult(body);
}

export interface WalkInSaleErrorView {
  message: string;
  retryable: boolean;
}

/**
 * Map a {@link createWalkInSale} failure to what the front desk sees. Branches on the envelope
 * `code` (never message text) and surfaces the server's own `message` (already brand-scrubbed via
 * `serverMessage`) where it is the more reliable signal — see the `INVALID_REQUEST` branch below.
 *
 * Negotiated-price failures (`OVERRIDE_NOT_SUPPORTED_ON_LINE` etc., DNKADM-0334/0335) are NOT
 * handled here — they are mapped by the SHARED `negotiatedPriceErrorView`
 * (`@/features/members/negotiatedPriceOverride`), which `WalkInSaleDialog` consults first, exactly
 * as `SellCartDialog`/`BuyMembershipOrderDialog` do. Keeping that mapping out of this api-layer
 * module avoids an api → feature import.
 */
export function toWalkInSaleErrorView(error: unknown): WalkInSaleErrorView {
  if (error instanceof CardTokenizationError) {
    return {
      retryable: false,
      message: error.validation
        ? 'Finish entering the card before charging.'
        : 'The card could not be tokenized. Ask the customer to check the card and try again.',
    };
  }
  if (!(error instanceof ApiError)) {
    return { retryable: false, message: 'The sale could not be completed.' };
  }
  const message = serverMessage(error);
  const code = v2ErrorCode(error);
  const suffix = error.requestId ? ` Request ID: ${error.requestId}` : '';

  // AdminWalkInSaleErrorCode.CONTACT_UNAVAILABLE (503) — the contact could not be provisioned
  // (fail-closed); no invite and no charge happened. Explicitly retryable, explicitly says so.
  if (code === 'CONTACT_UNAVAILABLE') {
    return {
      retryable: true,
      message: "Couldn't set up this customer — try again. Nothing was charged.",
    };
  }
  if (error.status === 402 || code === 'PAYMENT_DECLINED') {
    // DNKAPI-0676: a known decline reason replaces the plain decline line; OTHER/unknown keep it.
    const reasonLine = declineReasonLineFromError(error);
    return {
      retryable: false,
      message: `${reasonLine ?? message ?? 'Payment declined. Ask the customer for another card.'}${suffix}`,
    };
  }
  if (code === 'PROVIDER_NOT_CONFIGURED') {
    return {
      retryable: false,
      message: `${message ?? 'Payments are not configured for this location.'}${suffix}`,
    };
  }
  // DNKAPI-0497 — inherited from the order surface: a membership_plan line mixed with any
  // one-time (package/credit) line. The dialog's sale-kind picker makes this unreachable
  // through the UI, but the server's own message is shown verbatim as a backstop.
  if (code === 'UNSUPPORTED_IN_SLICE') {
    return {
      retryable: false,
      message: `${message ?? "That combination of items can't be sold together in one sale yet."}${suffix}`,
    };
  }
  if (code === 'INVALID_REQUEST' || error.status === 400) {
    // This surface OVERLOADS INVALID_REQUEST across a missing contact field
    // ("contact.email is required") and a rejected cart/payment shape ("quantity exceeds the
    // maximum purchasable…", "lines must not be empty") — the code alone cannot tell them
    // apart, only the server's own message can, so it is shown verbatim rather than guessed at.
    // Client-side validation already blocks the obvious missing-name/email case before submit;
    // this is the server's backstop for whatever slips through (a race, a cap that moved, etc.).
    return {
      retryable: false,
      message: `${message ?? 'Check the customer details and cart, then try again.'}${suffix}`,
    };
  }
  switch (error.code) {
    case 'SESSION_EXPIRED':
      return {
        retryable: false,
        message: `Your session expired. Please sign in again to continue.${suffix}`,
      };
    case 'FORBIDDEN':
    case 'NOT_FOUND':
    case 'VALIDATION':
    case 'CONFLICT':
    case 'UNKNOWN':
    case 'RATE_LIMITED':
    case 'NETWORK_ERROR':
      return {
        retryable: error.retryable === true,
        message: `${message ?? 'The sale could not be completed.'}${suffix}`,
      };
  }
  return { retryable: false, message: 'The sale could not be completed.' };
}
